import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Alert, Button } from '@mui/material';
import { AuthContext } from '../../context/auth-context';
import { projectActions } from '../../../store/project';
import DisciplinesAPI from '../../../services/api/ai/project-docs/disciplines-api';
import AssistantAPI from '../../../services/api/ai/project-docs/assistant-api';
import DocumentsAPI from '../../../services/api/ai/project-docs/documents-api';
import ScheduleAPI from '../../../services/api/ai/project-docs/schedule-api';
import { createRequestGenerationTracker } from '../../../utils/request-generation';
import { runWithLimitedConcurrency } from '../../../utils/concurrency';
import Logger from '../../../utils/logger';
import ConversationSidebar from './ConversationSidebar';
import ChatPanel from './ChatPanel';
import DocumentsPane, {
  DOCUMENTS_PANE_DEFAULT_WIDTH,
  isFileDrag,
} from './DocumentsPane';
import ColumnResizeHandle from './ColumnResizeHandle';
import BatchUploadDocumentDialog from './BatchUploadDocumentDialog';
import DocumentReviewDialog from './DocumentReviewDialog';
import DocumentVersionHistoryDialog from './DocumentVersionHistoryDialog';

// Deliberately small and fixed, matching this codebase's existing
// "small deterministic concurrency" convention rather than an unbounded
// Promise.all across an arbitrary number of selected files.
const BATCH_UPLOAD_CONCURRENCY = 3;

// Smallest widths the chat/documents divider may leave each pane: room for a
// readable chat composer, and for document titles and status chips.
const MIN_CHAT_COLUMN_WIDTH = 360;
const MIN_DOCUMENTS_PANE_WIDTH = 200;

// Live ingestion-progress polling cadence -- see the loadDocuments/
// isAnyDocumentProcessing effect below. Approximate, not a hard guarantee:
// each tick is scheduled only after the previous one's fetch resolves.
const DOCUMENT_POLL_INTERVAL_MS = 2000;

// A REAL ai_knowledge_disciplines row named "General Conditions" (used for
// tagging Division 00/01-style specs) is a completely separate concept from
// the synthetic PROJECT_WIDE conversation scope (disciplineId === null,
// which the composer displays as "General Conditions" too -- see
// disciplineDisplayName in discipline-badge.js) -- see the module docstring
// below. Normalized the same way the DB enforces
// its own name uniqueness (LOWER(BTRIM(name))), so this exclusion is name-
// based only and never derives/compares against that discipline's UUID.
const GENERAL_CONDITIONS_NAME = 'general conditions';
const isRealGeneralConditionsDiscipline = (discipline) =>
  (discipline.name || '').trim().toLowerCase() === GENERAL_CONDITIONS_NAME;

const extractErrorMessage = (err, fallback) =>
  err.response?.data?.message || fallback;

// Small, explicit per-schedule-type API lookup -- NOT a plugin registry:
// each entry just names the already-existing ScheduleAPI download method for
// that type, so the single shared "download a run" handler below can call
// the right one without a `scheduleType === 'ductwork' ? ... : ...` ladder
// growing every time a new schedule tool is added. Adding Electrical
// Material/Equipment later means adding one more entry here.
//
// FINAL PRODUCT DIRECTION: this is the ONLY schedule-run action the
// frontend still performs -- there is no "get one run's detail" action
// left (see ChatPanel.js's ToolResultCard docstring: Download is the one
// artifact action; the generic schedule-detail dialog was removed).
const SCHEDULE_DOWNLOAD_API_BY_TYPE = {
  piping: ScheduleAPI.downloadPipingScheduleRun,
  ductwork: ScheduleAPI.downloadDuctworkScheduleRun,
  electrical_material: ScheduleAPI.downloadElectricalMaterialScheduleRun,
};

/**
 * Saves a Blob the browser already has in memory (from an authenticated
 * fetch -- never a plain `<a href>` to an authenticated URL, which cannot
 * carry a bearer token) as a real file download, using a throwaway,
 * never-appended-to-the-visible-DOM-for-more-than-an-instant anchor
 * element. The object URL is revoked immediately after the click so it
 * cannot leak memory across repeated downloads.
 */
const triggerBrowserFileDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Opens an already-fetched document Blob in a new tab, deep-linked to a
 * specific page when one is known -- the `#page=N` URL fragment is a
 * standard convention Chrome/Edge's own built-in PDF viewer honors on a
 * blob: URL exactly as it would on a normal http(s) one; this is NOT a
 * custom viewer/highlighting engine, just navigation to the browser's
 * native one. Never fabricates a page: pageNumber is passed through
 * exactly as given (including null/undefined, which simply opens the
 * document with no page anchor at all) -- see this file's own callers for
 * where a real vs. missing page number is decided. The object URL is
 * intentionally NOT revoked immediately (unlike triggerBrowserFileDownload
 * above): the new tab keeps rendering it after this function returns, and
 * revoking here would break that render; it is naturally released when
 * that tab is closed/navigated away, matching how a browser already
 * reclaims blob: URLs whose owning document has gone away.
 */
const openDocumentInNewTab = (blob, pageNumber) => {
  const url = window.URL.createObjectURL(blob);
  const target = pageNumber ? `${url}#page=${pageNumber}` : url;
  window.open(target, '_blank', 'noopener');
};

// The async operation classes that can supersede one another within a
// single project scope. See client/src/utils/request-generation.js for the
// ownership mechanism.
const REQUEST_CHANNELS = [
  'conversations',
  'messages',
  'createConversation',
  'renameConversation',
  'askQuestion',
  'updateScope',
  'documents',
  'uploadDocument',
  'uploadDocumentBatch',
  'loadReview',
  'approveReview',
  'rejectReview',
  'retryDocument',
];

/**
 * ID-based upsert: replaces an existing entry with the same id in place, or
 * prepends a brand-new one. Used (instead of a blind prepend) wherever a
 * just-created/just-updated conversation is written back into `conversations`,
 * since an authoritative `listConversations` refetch can race the create/
 * update response and land first -- a blind prepend would then produce a
 * duplicate row for the same conversation id.
 */
const upsertConversationById = (list, conversation) => {
  const idx = list.findIndex((c) => c.id === conversation.id);
  if (idx === -1) return [conversation, ...list];
  const next = [...list];
  next[idx] = conversation;
  return next;
};

/**
 * Everything scoped to one project: conversations, messages, documents, and
 * upload. The browser never calls RAG directly: every request below goes to
 * a human-authenticated FieldTime endpoint (routes/ai/project-docs/
 * assistant-route.js and routes/ai/project-docs/documents-route.js),
 * which is what securely calls RAG server-side.
 *
 * The parent below mounts this with `key={selectedProject.id}`, so
 * switching the globally-selected project unmounts this entire instance
 * and mounts a brand-new one -- different closures, different refs,
 * different state, not a different render of the same instance. That is
 * what guarantees old-project results (a slow documents fetch, a
 * still-in-flight upload, a stale conversation list) can never populate the
 * new project: there is no shared instance left for a late response to
 * write into. A synchronous unmount cleanup effect (see requestTrackerRef
 * below -- a useLayoutEffect, not a useEffect) also invalidates every
 * channel at that same moment -- unmounting alone stops a stale result from
 * ever being RENDERED, but a promise continuation still runs regardless of
 * whether the component that started it is mounted, so invalidation is
 * what stops it from taking further action (e.g. an upload's success
 * handler re-fetching the document list) once the scope it belonged to is
 * gone.
 *
 * DISCIPLINE IS NO LONGER A PAGE-LEVEL SCOPE. There is no page-level
 * discipline gate, Select, or "select a discipline" message anymore -- the
 * three-pane row renders as soon as a project is selected. Instead, scope
 * lives at TWO finer-grained levels:
 *   - Documents (DocumentsPane): every document for the project is loaded
 *     at once and grouped client-side by type then by discipline (see
 *     document-type-groups.js) -- there is no discipline filter on the
 *     documents fetch at all anymore.
 *   - Assistant conversations: each persisted conversation carries TWO
 *     separate, required-but-nullable discipline fields (Migration 073) --
 *     `disciplineId`, its IMMUTABLE INITIAL classification (set once at
 *     creation, drives ONLY ConversationSidebar's badge, never changes
 *     again), and `searchScopeDisciplineId`, its MUTABLE CURRENT retrieval
 *     scope (drives the composer's scope selector and what the backend
 *     forwards to RAG; `null` means project-wide/"GEN COND"). Both start
 *     out equal -- a brand-new conversation's current scope IS its initial
 *     classification -- but only searchScopeDisciplineId can ever change
 *     afterward, via handleScopeChange below. The scope selector itself is
 *     rendered directly next to the composer (ChatPanel), never a
 *     page-level control. A not-yet-persisted "New Chat" draft
 *     (`conversationId === ''`) has its own local `draftDisciplineId`,
 *     defaulting to `null` (GEN COND); the conversation itself is only
 *     actually created, lazily, the first time the user sends a question
 *     from that draft (see sendDraftFirstTurn below), which seeds BOTH of
 *     the new conversation's discipline fields from that one value --
 *     `handleNewConversation` is a pure local reset, never an eager API
 *     call.
 *
 * A REAL ai_knowledge_disciplines row happening to be named "General
 * Conditions" is unrelated to PROJECT_WIDE/"GEN COND" -- it still appears
 * normally in DocumentsPane's discipline grouping, but is excluded from the
 * composer's scope-selector OPTIONS by name (see
 * isRealGeneralConditionsDiscipline above), purely to avoid two
 * same-labeled choices in that one dropdown. A conversation legitimately
 * scoped to that real discipline (however it got that way) still displays
 * and behaves as an ordinary discipline-scoped conversation everywhere else
 * (e.g. its sidebar badge shows its real name, never "GEN COND").
 *
 * STALE-RESPONSE SAFETY within one instance: every async request below is
 * owned by a monotonically increasing per-channel generation number from
 * requestTrackerRef (see client/src/utils/request-generation.js), captured
 * synchronously the instant the request starts. A request's success/error/
 * finally callback applies its result ONLY if its captured generation is
 * still the channel's current one; otherwise the result is silently
 * dropped.
 *
 * PROJECT-BOUNDARY SAFETY for the first-send/scope-change flows specifically
 * (sendQuestionTurn/sendDraftFirstTurn/handleScopeChange) is additionally
 * gated by `selectedProjectIdRef`, an always-current ref assigned during
 * render (not an effect) -- an explicit, request-generation-independent
 * check for "is the globally-selected project still the one this async
 * operation started under". Even though a project switch already unmounts
 * this whole instance (so its state setters become no-ops), this ref makes
 * that boundary explicit at each async continuation, and is what lets
 * list-level state (conversations[]) still be legitimately updated for a
 * SAME-project navigation while VIEW-level state (messages/pendingTurn) is
 * correctly withheld once the view has moved on.
 */
const ProjectScopedAssistant = ({
  auth,
  selectedProject,
  disciplines,
  disciplinesLoading,
}) => {
  const dispatch = useDispatch();

  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationId, setConversationId] = useState('');
  // Scope of the not-yet-persisted "New Chat" draft (conversationId === '').
  // Defaults to null (GEN COND / project-wide) on mount, after a project
  // switch, and after every New Chat reset.
  const [draftDisciplineId, setDraftDisciplineId] = useState(null);
  const [renamingConversationId, setRenamingConversationId] = useState(null);
  const [renameError, setRenameError] = useState('');

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [question, setQuestion] = useState('');
  // Explicit chat tool-picker selection (ChatToolPicker.js) for the
  // CURRENTLY DISPLAYED conversation-or-draft's composer -- `null` means
  // plain chat (askQuestion's existing purely natural-language-driven
  // behavior, unchanged). Purely UI-only draft state, exactly like
  // `question` above: reset on conversation switch/New Chat (see
  // handleSelectConversation/handleNewConversation), snapshotted into
  // `pendingTurn.tool` at Send time so a failed send's Retry reuses the
  // SAME tool it was originally sent with (see handleAskQuestion/
  // handleRetryQuestion), and cleared immediately on send -- never left
  // sitting in the live composer for a next, unrelated message to
  // accidentally inherit. Selecting a tool ONLY sets this state (see
  // handleSelectTool) -- it never itself sends anything; Send is always
  // what executes the turn, and a selected tool alone (blank typed text)
  // is already a complete, valid request (see handleAskQuestion's own
  // docstring below).
  const [selectedTool, setSelectedTool] = useState(null);
  // The in-flight/failed question turn for the CURRENTLY DISPLAYED
  // conversation-or-draft, rendered as a UI-only optimistic bubble -- never
  // a fake durable database record. Shape: { localId, question, tool,
  // searchScope, conversationId, projectId, status: 'pending' | 'failed' }.
  // `tool`/`searchScope`/`conversationId`/`projectId` are a SNAPSHOT
  // captured at Send time -- Retry always reuses this snapshot, never live
  // draftDisciplineId/composer-selector state (see handleRetryQuestion).
  // `conversationId` is `null` while the turn belongs to a still-uncreated
  // draft (creation itself may still be in flight or have failed) and
  // becomes the real id once creation succeeds. Exactly one at a time, by
  // design (KISS: one temporary chat state machine, not a queue).
  const [pendingTurn, setPendingTurn] = useState(null);
  // A small bounded memory of FAILED turns for conversations OTHER than
  // the one on screen, keyed by conversationId -- { [conversationId]:
  // <the same pendingTurn-shaped object, status always 'failed'> }. This
  // is what lets switching A -> B -> A restore A's failed question/Retry
  // action: the backend never persisted it (a turn is only ever saved
  // AFTER RAG succeeds), so if this map didn't exist, leaving the
  // conversation would destroy the user's only copy of a question they
  // already typed once. Never grows unbounded and never becomes a general
  // offline queue: an entry is written ONLY when a send/Retry for that
  // exact conversation fails while it is the one on screen, and is
  // deleted the moment that conversation's turn moves back to 'pending'
  // (a fresh send or Retry) -- never on mere navigation away. Keyed only by
  // a REAL conversation id -- a draft whose creation itself failed has no
  // id to key this map by, so its failed state lives only in `pendingTurn`
  // and is lost if the user navigates away from the draft (there is no
  // durable "draft" to return to).
  const [failedTurnsByConversation, setFailedTurnsByConversation] = useState(
    {}
  );
  const [error, setError] = useState('');

  // Composer scope-selector state for an EXISTING (persisted) conversation
  // only -- see handleScopeChange's own docstring below for the full
  // race-protection design. `pendingScopeSelection` uses `undefined` as its
  // "nothing pending" sentinel specifically because `null` is itself a
  // legitimate PENDING VALUE (switching to GEN COND).
  const [updatingScopeConversationId, setUpdatingScopeConversationId] =
    useState(null);
  const [pendingScopeSelection, setPendingScopeSelection] = useState(undefined);
  const [scopeUpdateError, setScopeUpdateError] = useState('');

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  // Files dropped on a Documents section ({ files, documentTypeId }), and a
  // counter that mounts a fresh Upload dialog for each drop.
  const [droppedUpload, setDroppedUpload] = useState(null);
  const [uploadDialogKey, setUploadDialogKey] = useState(0);
  const [documentsPaneWidth, setDocumentsPaneWidth] = useState(
    DOCUMENTS_PANE_DEFAULT_WIDTH
  );

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDocument, setReviewDocument] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewApproving, setReviewApproving] = useState(false);
  const [reviewRejecting, setReviewRejecting] = useState(false);

  const [retryingDocumentId, setRetryingDocumentId] = useState(null);
  // The document whose Version history dialog is open (null = closed). The
  // dialog owns its own requests -- see DocumentVersionHistoryDialog.js.
  const [versionsDocument, setVersionsDocument] = useState(null);

  // Project Assistant Schedule Tools -- FINAL PRODUCT DIRECTION: this is a
  // pure CHAT capability ("make the piping schedule", "rerun the ductwork
  // schedule", "download it", "upload it to RAG", ...) -- see
  // chat-schedule-tool-intent.js on the server. This page owns NO schedule
  // history/list state and performs NO history preload of any kind: there
  // is no Documents-pane affordance, no history dialog, and no run-detail
  // dialog to populate. The only schedule-related state left is a single
  // download-error slot for the one remaining action (downloading the
  // exact run named by a chat message's own toolResult.runId).
  const [scheduleDownloadError, setScheduleDownloadError] = useState('');
  // Clickable source navigation (a chat citation) -- a single shared error
  // slot, since only one "open a source document" action can realistically
  // be in flight from a user's own click at a time.
  const [sourceOpenError, setSourceOpenError] = useState('');

  // Lazily constructed once per instance; a project switch remounts this
  // whole component (see the module docstring above), so a new instance
  // always gets a brand-new tracker -- never a shared, reset-in-place one.
  const requestTrackerRef = useRef(null);
  if (!requestTrackerRef.current) {
    requestTrackerRef.current =
      createRequestGenerationTracker(REQUEST_CHANNELS);
  }

  // Always current, assigned during render (never inside an effect) -- see
  // the module docstring's "PROJECT-BOUNDARY SAFETY" section above.
  const selectedProjectIdRef = useRef(selectedProject?.id);
  selectedProjectIdRef.current = selectedProject?.id;

  // Invalidates every channel the INSTANT this project-scoped instance
  // unmounts (i.e. when the globally-selected project changes -- see the
  // module docstring). This is a useLayoutEffect, not a useEffect, so the
  // cleanup runs synchronously in the same commit that unmounts this
  // instance -- there is no gap between "this instance is gone" and "its
  // tracker is invalidated" for a stale promise continuation to slip
  // through.
  useLayoutEffect(() => {
    const tracker = requestTrackerRef.current;
    return () => {
      tracker.invalidateAll();
    };
  }, []);

  const loadConversations = useCallback(() => {
    const tracker = requestTrackerRef.current;
    if (!auth.token) {
      tracker.invalidate('conversations');
      setConversations([]);
      return;
    }
    const generation = tracker.begin('conversations');
    setConversationsLoading(true);
    AssistantAPI.listConversations(selectedProject.id, auth.token)
      .then((data) => {
        if (tracker.isCurrent('conversations', generation))
          setConversations(data);
      })
      .catch((err) => {
        if (tracker.isCurrent('conversations', generation)) {
          Logger.error('Error loading assistant conversations', err);
          setError(extractErrorMessage(err, 'Failed to load conversations.'));
        }
      })
      .finally(() => {
        if (tracker.isCurrent('conversations', generation))
          setConversationsLoading(false);
      });
  }, [selectedProject.id, auth.token]);

  // Fetches every conversation for the project once, on mount (and again
  // if the project/token identity changes, which never happens within one
  // instance -- see the module docstring's remount-per-project design).
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Mirrors `documents` at all times, including synchronously at the exact
  // moment loadDocuments itself calls setDocuments -- so the recursive
  // polling cycle below can always read the LATEST known document state
  // from an async continuation, however many renders/effect-flushes behind
  // it may be, without depending on `documents` changing to be woken up
  // again. See the polling effect's own docstring for why that matters.
  const documentsRef = useRef(documents);

  // `background: true` (used only by the live-progress poll below) skips
  // the documentsLoading toggle entirely, so a poll tick never flashes the
  // pane back into its full loading spinner -- only document data/error
  // state updates quietly in place. An initial/manual load (the default)
  // keeps its existing loading-spinner behavior unchanged. Both paths
  // share the SAME request, the SAME generation-tracker channel, and the
  // SAME scope fencing -- a background poll is not a second, separately
  // guarded code path.
  //
  // Returns the request's own promise chain -- ALWAYS resolving, never
  // rejecting, since the .catch() below never re-throws -- so a caller
  // (specifically the polling cycle) can schedule its next step in a
  // .finally() that runs whether the request succeeded or failed.
  const loadDocuments = useCallback(
    ({ background = false } = {}) => {
      const tracker = requestTrackerRef.current;
      if (!auth.token) {
        tracker.invalidate('documents');
        setDocuments([]);
        documentsRef.current = [];
        return Promise.resolve();
      }
      const generation = tracker.begin('documents');
      if (!background) {
        setDocumentsLoading(true);
        setDocumentsError('');
      }
      return DocumentsAPI.listProjectDocuments(selectedProject.id, auth.token)
        .then((data) => {
          if (tracker.isCurrent('documents', generation)) {
            setDocuments(data);
            documentsRef.current = data;
            // A background poll that succeeds after a prior error proves
            // the transient problem has cleared -- surface that quietly
            // too, without ever touching documentsLoading.
            if (background) setDocumentsError('');
          }
        })
        .catch((err) => {
          if (tracker.isCurrent('documents', generation)) {
            Logger.error('Error loading project documents', err);
            setDocumentsError(
              extractErrorMessage(err, 'Failed to load documents.')
            );
          }
          // Deliberately NOT re-thrown -- a failed background poll must
          // not be treated as an unhandled rejection, and must still let
          // the polling cycle's .finally() below run so it can retry.
        })
        .finally(() => {
          if (tracker.isCurrent('documents', generation) && !background)
            setDocumentsLoading(false);
        });
    },
    [selectedProject.id, auth.token]
  );

  // Fetched independently of conversations -- the two are unrelated
  // resources sharing only the same project scope.
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // LIVE INGESTION PROGRESS: while at least one document is still
  // "processing", quietly refresh the document list roughly every
  // DOCUMENT_POLL_INTERVAL_MS via the SAME loadDocuments/endpoint/tracker
  // above -- never a second progress model, never WebSockets/SSE.
  //
  // A genuine recursive async cycle, NOT effect-dependency-driven: the
  // next tick is scheduled inside THIS cycle's own .finally(), once the
  // current background request has fully settled -- success or failure --
  // rather than depending on `documents` changing to re-run this effect
  // and schedule the next timeout. Reading documentsRef.current (rather
  // than the `documents` closed over when the cycle started) at each step
  // is what avoids a stale closure -- the cycle always asks "is anything
  // STILL known to be processing right now", never "was anything
  // processing when I was scheduled".
  //
  // Still non-overlapping: the next request is only ever issued after the
  // previous one's .finally() runs, i.e. strictly after it has settled.
  // Still stops automatically once nothing is processing anymore --
  // including a document moving to error/blocked/review_required.
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const isProcessing = (docs) =>
      docs.some(
        (document) => document.processing_status?.status === 'processing'
      );

    const scheduleNextTick = () => {
      if (cancelled) return;
      timeoutId = setTimeout(runTick, DOCUMENT_POLL_INTERVAL_MS);
    };

    function runTick() {
      if (cancelled) return;
      if (!isProcessing(documentsRef.current)) return;
      loadDocuments({ background: true }).finally(scheduleNextTick);
    }

    if (isProcessing(documentsRef.current)) {
      scheduleNextTick();
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [documents, loadDocuments]);

  // Downloads one saved run as CSV -- reusable from a chat message's own
  // tool-result card (see handleDownloadToolResult below), the ONE
  // remaining schedule-run action on this page (FINAL PRODUCT DIRECTION:
  // no history list, no run-detail dialog -- see this file's own module
  // docstring). Never reconstructs the schedule client-side: the file
  // comes directly from the SAME persisted run the server already has
  // (see schedule-export.js's own docstring).
  const handleDownloadScheduleRun = async (scheduleType, run) => {
    setScheduleDownloadError('');
    try {
      const { blob, fileName } = await SCHEDULE_DOWNLOAD_API_BY_TYPE[
        scheduleType
      ](selectedProject.id, run.id, auth.token);
      triggerBrowserFileDownload(blob, fileName);
    } catch (err) {
      Logger.error('Error downloading schedule run', err, { scheduleType });
      setScheduleDownloadError(
        extractErrorMessage(err, 'Failed to download this schedule run.')
      );
    }
  };

  // A chat message's tool-result card only ever carries {tool, runId, ...}
  // metadata (see Migration 076's toolResult shape), never a full run
  // object -- handleDownloadScheduleRun only ever reads `run.id`, so a
  // minimal `{ id: toolResult.runId }` is exactly as good as the real thing.
  const handleDownloadToolResult = (toolResult) => {
    handleDownloadScheduleRun(toolResult.tool, { id: toolResult.runId });
  };

  // Clickable source navigation -- a chat citation footer entry. Passes an
  // internal, never-displayed documentId/documentVersionId plus an optional
  // page number; this fetches the real file bytes through the authenticated
  // FieldTime endpoint (never a raw Blob URL/SAS token) and opens it in a
  // new tab, deep-linked to that page when known. A fetch failure is
  // reported inline via sourceOpenError rather than silently doing
  // nothing, but is deliberately NOT tracked through the request-generation
  // tracker used elsewhere on this page -- it never populates any
  // per-conversation/per-project VIEW state that a project switch could
  // race, it only opens a new browser tab.
  //
  // fetchAndOpenDocumentVersion is the shared fetch-and-open step; it
  // rejects on failure so each caller reports the error where the user
  // clicked (the chat here, or the Version history dialog).
  const fetchAndOpenDocumentVersion = async (
    documentId,
    documentVersionId,
    pageNumber
  ) => {
    const { blob } = await DocumentsAPI.getDocumentVersionFile(
      selectedProject.id,
      documentId,
      documentVersionId,
      auth.token
    );
    openDocumentInNewTab(blob, pageNumber);
  };

  const handleOpenSourceDocument = async (
    documentId,
    documentVersionId,
    pageNumber
  ) => {
    setSourceOpenError('');
    try {
      await fetchAndOpenDocumentVersion(
        documentId,
        documentVersionId,
        pageNumber
      );
    } catch (err) {
      Logger.error('Error opening source document', err);
      setSourceOpenError(
        extractErrorMessage(err, 'Failed to open this document.')
      );
    }
  };

  const handleOpenCitation = (citation) => {
    if (!citation || !citation.documentId || !citation.documentVersion) return;
    const pageNumber =
      Array.isArray(citation.pageNumbers) && citation.pageNumbers.length > 0
        ? citation.pageNumbers[0]
        : null;
    handleOpenSourceDocument(
      citation.documentId,
      citation.documentVersion,
      pageNumber
    );
  };

  const loadMessages = useCallback(
    (targetConversationId) => {
      if (!targetConversationId) return;
      const tracker = requestTrackerRef.current;
      const generation = tracker.begin('messages');
      setMessagesLoading(true);
      AssistantAPI.getMessages(
        selectedProject.id,
        targetConversationId,
        auth.token
      )
        .then(({ messages: fetchedMessages }) => {
          if (tracker.isCurrent('messages', generation))
            setMessages(fetchedMessages);
        })
        .catch((err) => {
          if (tracker.isCurrent('messages', generation)) {
            Logger.error('Error loading assistant messages', err);
            setError(
              extractErrorMessage(err, 'Failed to load conversation messages.')
            );
          }
        })
        .finally(() => {
          if (tracker.isCurrent('messages', generation))
            setMessagesLoading(false);
        });
    },
    [selectedProject.id, auth.token]
  );

  // Conversation selection is a direct user event -- loadMessages is called
  // synchronously, right here, so its generation is captured before
  // anything else can race it. Messages are cleared synchronously too (not
  // just left to the new fetch's eventual success): otherwise, if the
  // newly-selected conversation's own getMessages request fails, the
  // PREVIOUS conversation's messages would still be sitting in state (its
  // `.catch()` only sets an error, it never clears `messages`) and would
  // incorrectly keep rendering under the newly-selected conversation. The
  // draft question is cleared too: it was written for whichever
  // conversation/draft was previously on screen, not this one.
  //
  // Invalidates 'createConversation' too (in addition to 'messages'/
  // 'askQuestion'), so selecting an existing conversation correctly
  // discards/detaches from an in-flight draft-creation -- its own
  // continuation (sendDraftFirstTurn) will still legitimately upsert the
  // created conversation into the LIST once it resolves (list-level state
  // always applies within the same project), but must not touch view-level
  // state for whatever is now on screen. Also invalidates 'updateScope' and
  // resets its three pieces of state, so a scope edit in flight for the
  // conversation being navigated AWAY FROM cannot leave stray state behind
  // that resurfaces if the user returns to it later.
  //
  // pendingTurn is restored from failedTurnsByConversation, not simply
  // cleared: if the conversation being switched TO has a preserved failed
  // turn (see that map's own docstring above), its Failed bubble + Retry
  // action must reappear immediately.
  const handleSelectConversation = (id) => {
    const tracker = requestTrackerRef.current;
    tracker.invalidate('messages');
    tracker.invalidate('askQuestion');
    tracker.invalidate('createConversation');
    tracker.invalidate('updateScope');
    setError('');
    setQuestion('');
    setSelectedTool(null);
    setPendingTurn(failedTurnsByConversation[id] || null);
    setMessages([]);
    setConversationId(id);
    setUpdatingScopeConversationId(null);
    setPendingScopeSelection(undefined);
    setScopeUpdateError('');
    loadMessages(id);
  };

  // Pure local reset -- NO API call. Landing in the draft state
  // (conversationId === '') is a fully valid, permanently-usable state, not
  // a transient gap: the conversation is only actually created, lazily, the
  // first time a question is sent from it (see sendDraftFirstTurn below).
  const handleNewConversation = () => {
    const tracker = requestTrackerRef.current;
    tracker.invalidate('messages');
    tracker.invalidate('askQuestion');
    tracker.invalidate('createConversation');
    tracker.invalidate('updateScope');
    setConversationId('');
    setDraftDisciplineId(null);
    setMessages([]);
    setMessagesLoading(false);
    setQuestion('');
    setSelectedTool(null);
    setPendingTurn(null);
    setUpdatingScopeConversationId(null);
    setPendingScopeSelection(undefined);
    setScopeUpdateError('');
    setError('');
  };

  // Manual rename, tracked the same way as createConversation/askQuestion.
  // Neither of a conversation's two discipline fields is part of the
  // rename request at all (see assistant-controller.js/assistant-api.js) --
  // rename only ever touches title. The existence guard below is a plain
  // client-side UX check (avoid an API call for an id no longer in local
  // state), mirroring handleAskQuestion's own missing-conversation guard.
  const handleRenameConversation = async (id, title) => {
    const existingConversation = conversations.find((c) => c.id === id);
    if (!existingConversation) {
      setRenameError('Unable to determine this conversation. Please refresh.');
      return false;
    }
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('renameConversation');
    setRenamingConversationId(id);
    setRenameError('');
    try {
      const conversation = await AssistantAPI.renameConversation(
        selectedProject.id,
        id,
        title,
        auth.token
      );
      if (!tracker.isCurrent('renameConversation', generation)) {
        return false;
      }
      setConversations((prev) =>
        prev.map((existing) => (existing.id === id ? conversation : existing))
      );
      return true;
    } catch (err) {
      if (tracker.isCurrent('renameConversation', generation)) {
        Logger.error('Error renaming assistant conversation', err);
        setRenameError(
          extractErrorMessage(err, 'Failed to rename conversation.')
        );
      }
      return false;
    } finally {
      if (tracker.isCurrent('renameConversation', generation)) {
        setRenamingConversationId(null);
      }
    }
  };

  // The ONE place that actually calls the askQuestion API, shared by an
  // initial send on an EXISTING conversation, a Retry of an ask-only
  // failure, and the follow-up call sendDraftFirstTurn makes once its own
  // conversation creation succeeds. Never throws: every failure is caught
  // and turned into pendingTurn's 'failed' state (when applyToUI), so
  // callers never need their own try/catch.
  //
  // `applyToUI` (default true) controls whether this call is allowed to
  // mutate VIEW-level state (messages/pendingTurn/error) -- it is false
  // only when sendDraftFirstTurn determines the view has already moved on
  // to something else by the time creation resolved, but the question
  // itself still deserves to be durably asked. LIST-level state
  // (conversations[]' title-on-first-answer, and failedTurnsByConversation
  // on failure) always applies as long as the request resolved for the
  // SAME project -- see this function's own project-boundary check below.
  //
  // `localId` is the optimistic bubble's own local-only identifier -- it is
  // NEVER sent to the server and never becomes a durable database id; it
  // only lets this function find its OWN pendingTurn slot again (and
  // silently no-op if that slot no longer belongs to it).
  //
  // `searchScope` is carried through ONLY so a failure can preserve a
  // COMPLETE pendingTurn-shaped snapshot ({ localId, question, tool,
  // searchScope, conversationId, status }) into failedTurnsByConversation --
  // the same full shape handleAskQuestion originally wrote into pendingTurn.
  // Without it, a turn restored from that map after switching away and back
  // would be missing searchScope/could not be told apart from a draft on
  // retry; it is never sent to askQuestion itself (the server always uses
  // the conversation's own stored scope, never a client-supplied one).
  // `tool` (the composer's explicit tool-picker selection at Send time, or
  // `null`) IS forwarded to askQuestion -- unlike searchScope, it is a real
  // part of this request (see AssistantAPI.askQuestion's own docstring) --
  // and is ALSO preserved in the failure snapshot so Retry resends the
  // EXACT same tool selection, never silently reverting to plain chat.
  const sendQuestionTurn = async (
    localId,
    targetConversationId,
    trimmed,
    originatingProjectId,
    searchScope,
    tool,
    { applyToUI = true } = {}
  ) => {
    const tracker = requestTrackerRef.current;
    const generation = applyToUI ? tracker.begin('askQuestion') : null;
    if (applyToUI) setError('');
    try {
      const { userMessage, assistantMessage, conversationTitle } =
        await AssistantAPI.askQuestion(
          originatingProjectId,
          targetConversationId,
          trimmed,
          auth.token,
          tool
        );

      if (originatingProjectId !== selectedProjectIdRef.current) {
        // Different project now -- never mutate ANYTHING (list or view).
        // The work still completed/persisted server-side; that project
        // will be rediscovered via its own authoritative refetch whenever
        // the user returns to it.
        return;
      }

      // Same project: list-level title-on-first-answer always applies.
      if (conversationTitle) {
        setConversations((prev) =>
          prev.map((existing) =>
            existing.id === targetConversationId
              ? { ...existing, title: conversationTitle }
              : existing
          )
        );
      }

      if (!applyToUI || !tracker.isCurrent('askQuestion', generation)) {
        // Superseded (a different conversation/draft is now on screen, or
        // this call was never allowed to touch the view to begin with) --
        // the answer was durably saved server-side, but must not be
        // appended to whatever is currently on screen.
        return;
      }

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setPendingTurn((prev) =>
        prev && prev.localId === localId ? null : prev
      );
    } catch (err) {
      if (originatingProjectId !== selectedProjectIdRef.current) {
        return;
      }

      Logger.error('Error asking Project Documents Assistant question', err);

      // Same project: failedTurnsByConversation is list-level durable
      // memory and always applies, but only when there is a real
      // conversation id to key it by (a still-uncreated draft's failure is
      // handled entirely by sendDraftFirstTurn's own catch, never here).
      if (targetConversationId) {
        const failedTurn = {
          localId,
          question: trimmed,
          tool,
          searchScope,
          conversationId: targetConversationId,
          projectId: originatingProjectId,
          status: 'failed',
        };
        setFailedTurnsByConversation((prev) => ({
          ...prev,
          [targetConversationId]: failedTurn,
        }));
      }

      if (!applyToUI || !tracker.isCurrent('askQuestion', generation)) {
        return;
      }

      setError(
        extractErrorMessage(
          err,
          'Something went wrong. Please contact an administrator.'
        )
      );
      setPendingTurn((prev) =>
        prev && prev.localId === localId ? { ...prev, status: 'failed' } : prev
      );
    }
  };

  // Lazily creates the conversation a draft's FIRST question belongs to,
  // then asks that question through the exact same sendQuestionTurn used
  // for every later turn. See the module docstring's "PROJECT-BOUNDARY
  // SAFETY" section for `selectedProjectIdRef`; see upsertConversationById
  // above for why the new conversation is upserted rather than blindly
  // prepended.
  const sendDraftFirstTurn = async (
    localId,
    trimmed,
    searchScope,
    originatingProjectId,
    tool
  ) => {
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('createConversation');
    let conversation;
    try {
      conversation = await AssistantAPI.createConversation(
        originatingProjectId,
        searchScope,
        auth.token
      );
    } catch (err) {
      if (
        originatingProjectId === selectedProjectIdRef.current &&
        tracker.isCurrent('createConversation', generation)
      ) {
        Logger.error('Error creating assistant conversation', err);
        setError(
          extractErrorMessage(err, 'Failed to start a new conversation.')
        );
        setPendingTurn((prev) =>
          prev && prev.localId === localId
            ? { ...prev, status: 'failed', conversationId: null }
            : prev
        );
      }
      return;
    }

    if (originatingProjectId !== selectedProjectIdRef.current) {
      // Different project -- do not touch conversations[]/conversationId/
      // pendingTurn for this project at all. The question the user
      // actually submitted is still legitimate durable work, just applied
      // with applyToUI: false (nothing left on screen to reconcile into).
      sendQuestionTurn(
        localId,
        conversation.id,
        trimmed,
        originatingProjectId,
        searchScope,
        tool,
        { applyToUI: false }
      );
      return;
    }

    // Same project: list-level upsert always applies -- this is real,
    // durable conversation history regardless of what's currently
    // selected; suppressing it would create a ghost that reappears
    // unexplained on the next authoritative refetch.
    setConversations((prev) => upsertConversationById(prev, conversation));
    const stillSameConversationView = tracker.isCurrent(
      'createConversation',
      generation
    );
    if (stillSameConversationView) {
      setConversationId(conversation.id);
      setPendingTurn((prev) =>
        prev && prev.localId === localId
          ? { ...prev, conversationId: conversation.id }
          : prev
      );
    }
    sendQuestionTurn(
      localId,
      conversation.id,
      trimmed,
      originatingProjectId,
      searchScope,
      tool,
      { applyToUI: stillSameConversationView }
    );
  };

  // On a valid send: resolve the search scope FAIL-CLOSED (never default a
  // missing local conversation lookup to project-wide -- see
  // existingConversation's own check below), snapshot everything the send
  // needs (question/tool/scope/conversation id/originating project) into
  // `pendingTurn`, clear the composer immediately (both the typed text AND
  // the selected tool -- see setSelectedTool(null) below, so the NEXT,
  // unrelated message never inherits this one's tool), render the
  // optimistic bubble, and dispatch to whichever path applies. A selected
  // tool alone (blank typed text) is already a complete, valid request --
  // see ChatToolPicker.js's own docstring -- so the guard below only blocks
  // sending when there is NEITHER typed text NOR a selected tool.
  // `pendingTurn` truthy is what disables the composer/Send/tool picker
  // (passed to ChatPanel as `submitting`), so this also prevents a
  // double-send; a scope update in flight for the SAME conversation blocks
  // sending too (its scope is momentarily ambiguous).
  const handleAskQuestion = () => {
    const trimmed = question.trim();
    if (
      (!trimmed && !selectedTool) ||
      pendingTurn ||
      (conversationId && updatingScopeConversationId === conversationId)
    ) {
      return;
    }

    const existingConversation = conversationId
      ? conversations.find((c) => c.id === conversationId)
      : undefined;
    // Fail-closed: a missing conversation, or one found but missing its own
    // searchScopeDisciplineId field (never a legitimate state -- the server
    // always returns either null or a UUID), is never silently treated as
    // project-wide. `null` (project-wide/GEN COND) IS a known-valid scope and
    // must pass this check.
    if (
      conversationId &&
      (!existingConversation ||
        existingConversation.searchScopeDisciplineId === undefined)
    ) {
      setError(
        "Unable to determine this conversation's search scope. Please refresh."
      );
      return;
    }
    // Only the temporary draft ever falls back to draftDisciplineId; an
    // existing conversation always uses its own CURRENT search scope, never
    // its separate, immutable disciplineId (initial classification).
    const searchScope = conversationId
      ? existingConversation.searchScopeDisciplineId
      : draftDisciplineId;

    const originatingProjectId = selectedProject.id;
    const localId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const tool = selectedTool;

    setQuestion('');
    setSelectedTool(null);
    setError('');
    setPendingTurn({
      localId,
      question: trimmed,
      tool,
      searchScope,
      conversationId: conversationId || null,
      projectId: originatingProjectId,
      status: 'pending',
    });

    if (conversationId) {
      sendQuestionTurn(
        localId,
        conversationId,
        trimmed,
        originatingProjectId,
        searchScope,
        tool
      );
    } else {
      sendDraftFirstTurn(
        localId,
        trimmed,
        searchScope,
        originatingProjectId,
        tool
      );
    }
  };

  // Retry reads ONLY the snapshotted pendingTurn -- never live
  // draftDisciplineId/composer-selector state, which may have changed since
  // the original failed attempt. Branches on the snapshot's own
  // conversationId: null means creation itself failed (retry re-invokes
  // creation with the ORIGINAL scope); a real id means only askQuestion
  // failed (creation already durably succeeded and must never be
  // re-attempted -- retry calls sendQuestionTurn again).
  const handleRetryQuestion = (localId) => {
    if (
      !pendingTurn ||
      pendingTurn.localId !== localId ||
      pendingTurn.status !== 'failed'
    ) {
      return;
    }
    const {
      question: trimmed,
      tool,
      searchScope,
      conversationId: snapshotConversationId,
      projectId: originatingProjectId,
    } = pendingTurn;

    setPendingTurn((prev) =>
      prev && prev.localId === localId ? { ...prev, status: 'pending' } : prev
    );
    if (snapshotConversationId) {
      setFailedTurnsByConversation((prev) => {
        if (!(snapshotConversationId in prev)) return prev;
        const next = { ...prev };
        delete next[snapshotConversationId];
        return next;
      });
      sendQuestionTurn(
        localId,
        snapshotConversationId,
        trimmed,
        originatingProjectId,
        searchScope,
        tool
      );
    } else {
      sendDraftFirstTurn(
        localId,
        trimmed,
        searchScope,
        originatingProjectId,
        tool
      );
    }
  };

  // Composer scope selector for an EXISTING (persisted) conversation. Only
  // ever changes search_scope_discipline_id server-side -- never
  // discipline_id, the conversation's separate, immutable initial
  // classification -- so the sidebar badge (which reads disciplineId only,
  // see ConversationSidebar.js) never changes as a result of this flow, no
  // matter how it resolves. pendingScopeSelection is the ONE piece of
  // optimistic-looking state this flow writes -- it is scoped ONLY to the
  // selector control itself (see displayedSelectorScope below); the
  // sidebar badge always reads straight from `conversations[]`'s
  // disciplineId and is never influenced by it. Full bidirectional race
  // protection: a same-project conversation switch while this is in flight
  // leaves the newly-selected conversation's state untouched while the
  // ORIGINATING conversation's list-level row (searchScopeDisciplineId,
  // reflected the next time that conversation is reopened -- never its
  // badge) still updates on eventual success; a cross-project switch
  // touches nothing in the new project.
  const handleScopeChange = async (newDisciplineId) => {
    if (!conversationId || updatingScopeConversationId || pendingTurn) return;
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('updateScope');
    const originatingProjectId = selectedProject.id;
    const targetConversationId = conversationId;
    setScopeUpdateError('');
    setUpdatingScopeConversationId(targetConversationId);
    setPendingScopeSelection(newDisciplineId);
    try {
      const updated = await AssistantAPI.updateConversationScope(
        originatingProjectId,
        targetConversationId,
        newDisciplineId,
        auth.token
      );
      if (originatingProjectId !== selectedProjectIdRef.current) return;
      setConversations((prev) =>
        prev.map((c) => (c.id === targetConversationId ? updated : c))
      );
      if (tracker.isCurrent('updateScope', generation)) {
        setPendingScopeSelection(undefined);
      }
    } catch (err) {
      if (
        originatingProjectId === selectedProjectIdRef.current &&
        tracker.isCurrent('updateScope', generation)
      ) {
        Logger.error('Error updating assistant conversation scope', err);
        setScopeUpdateError(
          extractErrorMessage(err, 'Failed to update search scope.')
        );
        setPendingScopeSelection(undefined);
      }
    } finally {
      if (
        originatingProjectId === selectedProjectIdRef.current &&
        tracker.isCurrent('updateScope', generation)
      ) {
        setUpdatingScopeConversationId(null);
      }
    }
  };

  const existingConversationForSelector = conversationId
    ? conversations.find((c) => c.id === conversationId)
    : undefined;
  // Fail-closed, same principle as handleAskQuestion's own guard above: a
  // conversationId with no matching row in local state is UNKNOWN, never
  // silently displayed as project-wide. This reads the conversation's
  // CURRENT search scope (searchScopeDisciplineId), never its separate,
  // immutable disciplineId (initial classification -- that only ever drives
  // ConversationSidebar's badge). `undefined` (distinct from a real, found
  // conversation's `searchScopeDisciplineId` of `null`, which IS
  // project-wide) is passed straight through to ScopeSelector, which
  // renders it as no selection rather than collapsing it into the GEN COND
  // option -- this covers a found conversation missing that field entirely,
  // which must also never be treated as project-wide.
  const displayedSelectorScope =
    pendingScopeSelection !== undefined
      ? pendingScopeSelection
      : conversationId && existingConversationForSelector
        ? existingConversationForSelector.searchScopeDisciplineId
        : undefined;
  const currentComposerScopeValue = conversationId
    ? displayedSelectorScope
    : draftDisciplineId;

  // Composer scope-selector options: the synthetic PROJECT_WIDE entry
  // ("General Conditions", rendered by ChatPanel itself, pinned first) plus
  // every active discipline EXCEPT a real "General Conditions" row -- see the
  // module docstring's own explanation of why these are unrelated concepts.
  // EXCEPTION: if the conversation/draft currently on screen is ALREADY
  // scoped to that real discipline (however it got that way), it is added
  // back in so MUI's Select can still render its label as the current
  // value -- it is displayed, never newly offered as a fresh choice
  // alongside GEN COND (which is the actual source of confusion this
  // exclusion guards against).
  const composerDisciplineOptions = useMemo(() => {
    const filtered = disciplines.filter(
      (d) => !isRealGeneralConditionsDiscipline(d)
    );
    if (
      currentComposerScopeValue &&
      !filtered.some((d) => d.id === currentComposerScopeValue)
    ) {
      const currentMatch = disciplines.find(
        (d) => d.id === currentComposerScopeValue
      );
      if (currentMatch) return [...filtered, currentMatch];
    }
    return filtered;
  }, [disciplines, currentComposerScopeValue]);

  // Files dropped on a Documents section open the same Upload dialog, with
  // those files already added as that section's type.
  const handleFilesDropped = (documentTypeId, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0 || uploadDialogOpen) return;
    setDroppedUpload({ files, documentTypeId });
    setUploadDialogKey((key) => key + 1);
    setUploadDialogOpen(true);
  };

  // While this page is open, a file dropped anywhere outside a Documents
  // section does nothing, instead of the browser opening the file in place
  // of FieldTime.
  useEffect(() => {
    const ignoreFileDrop = (event) => {
      if (isFileDrag(event)) event.preventDefault();
    };
    window.addEventListener('dragover', ignoreFileDrop);
    window.addEventListener('drop', ignoreFileDrop);
    return () => {
      window.removeEventListener('dragover', ignoreFileDrop);
      window.removeEventListener('drop', ignoreFileDrop);
    };
  }, []);

  // The one Upload workflow (BatchUploadDocumentDialog), for one file or
  // many. Each item goes through the single-document API call
  // (DocumentsAPI.uploadDocument) -- never a batch endpoint -- with bounded
  // concurrency (BATCH_UPLOAD_CONCURRENCY at a time, never an unbounded
  // Promise.all across every selected file); one file's failure never aborts
  // the others. Each item carries its own `disciplineId` (the SAME value for
  // the whole batch, chosen once in the dialog); there is no page-level
  // discipline.
  //
  // Afterward this deliberately RE-FETCHES the document list via the
  // existing loadDocuments() rather than splicing the raw upload responses
  // into local state: createManualDocument's own return shape is camelCase
  // and carries no processing_status/discipline_name at all -- entirely
  // different from listProjectDocuments'/getProjectDocument's snake_case
  // rows this pane already renders.
  const handleUploadDocumentBatch = async (items, onItemSettled) => {
    if (items.length === 0) return;
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('uploadDocumentBatch');

    await runWithLimitedConcurrency(
      items,
      BATCH_UPLOAD_CONCURRENCY,
      (item) =>
        DocumentsAPI.uploadDocument(
          selectedProject.id,
          {
            disciplineId: item.disciplineId,
            documentTypeId: item.documentTypeId,
            title: item.title,
            file: item.file,
          },
          auth.token
        ),
      (item, _index, outcome) => {
        onItemSettled(item, {
          success: outcome.ok,
          errorMessage: outcome.ok
            ? undefined
            : extractErrorMessage(outcome.error, 'Failed to upload document.'),
        });
      }
    );

    if (!tracker.isCurrent('uploadDocumentBatch', generation)) {
      return;
    }
    Logger.userAction('Uploaded Project Documents batch', {
      projectId: selectedProject.id,
      fileCount: items.length,
    });
    loadDocuments();
  };

  // Opens the Review dialog for one document and loads its current review
  // decision. disciplineId is sourced from the document's OWN row (every
  // document belongs to exactly one real discipline) -- there is no
  // page-level discipline anymore.
  const handleReviewClick = (document) => {
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('loadReview');
    setReviewDocument(document);
    setReviewDialogOpen(true);
    setReview(null);
    setReviewError('');
    setReviewLoading(true);
    DocumentsAPI.getDocumentReview(
      selectedProject.id,
      document.id,
      document.current_version_id,
      document.discipline_id,
      auth.token
    )
      .then((data) => {
        if (tracker.isCurrent('loadReview', generation)) setReview(data);
      })
      .catch((err) => {
        if (tracker.isCurrent('loadReview', generation)) {
          Logger.error('Error loading document review', err);
          setReviewError(
            extractErrorMessage(err, 'Failed to load review details.')
          );
        }
      })
      .finally(() => {
        if (tracker.isCurrent('loadReview', generation))
          setReviewLoading(false);
      });
  };

  // Closing the dialog (the Close button, or the backdrop once nothing is
  // in flight) supersedes any still-pending load/approve/reject for this
  // dialog instance -- a stale response arriving afterward must never
  // reopen it or repopulate stale review data into whatever the user opens
  // next.
  const handleCloseReviewDialog = () => {
    const tracker = requestTrackerRef.current;
    tracker.invalidate('loadReview');
    tracker.invalidate('approveReview');
    tracker.invalidate('rejectReview');
    setReviewDialogOpen(false);
    setReviewDocument(null);
    setReview(null);
    setReviewError('');
    setReviewLoading(false);
    setReviewApproving(false);
    setReviewRejecting(false);
  };

  // Approve: on success, FieldTime has already durably created a NEW
  // ingestion dispatch for the same document version and started
  // delivering it server-side, so the dialog closes immediately and
  // loadDocuments() re-fetches -- the existing ~2s processing-poll effect
  // above picks the document back up automatically. disciplineId comes
  // from the document being reviewed, same as handleReviewClick above.
  const handleApproveReview = async (reviewerNote) => {
    if (!reviewDocument || !review || reviewApproving || reviewRejecting) {
      return;
    }
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('approveReview');
    setReviewApproving(true);
    setReviewError('');
    try {
      await DocumentsAPI.approveDocumentReview(
        selectedProject.id,
        reviewDocument.id,
        reviewDocument.current_version_id,
        review.id,
        reviewDocument.discipline_id,
        reviewerNote,
        auth.token
      );
      if (!tracker.isCurrent('approveReview', generation)) return;
      Logger.userAction('Approved Project Documents review', {
        projectId: selectedProject.id,
        documentId: reviewDocument.id,
      });
      setReviewDialogOpen(false);
      setReviewDocument(null);
      setReview(null);
      loadDocuments();
    } catch (err) {
      if (tracker.isCurrent('approveReview', generation)) {
        Logger.error('Error approving document review', err);
        setReviewError(extractErrorMessage(err, 'Failed to approve review.'));
      }
    } finally {
      if (tracker.isCurrent('approveReview', generation))
        setReviewApproving(false);
    }
  };

  // Reject creates no new dispatch server-side, but the review decision
  // itself is durable and documents-db.js projects it into
  // processing_status.status as 'review_rejected' on read -- so re-fetching
  // here is what shows "Review Rejected" immediately.
  const handleRejectReview = async (reviewerNote) => {
    if (!reviewDocument || !review || reviewApproving || reviewRejecting) {
      return;
    }
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('rejectReview');
    setReviewRejecting(true);
    setReviewError('');
    try {
      await DocumentsAPI.rejectDocumentReview(
        selectedProject.id,
        reviewDocument.id,
        reviewDocument.current_version_id,
        review.id,
        reviewDocument.discipline_id,
        reviewerNote,
        auth.token
      );
      if (!tracker.isCurrent('rejectReview', generation)) return;
      Logger.userAction('Rejected Project Documents review', {
        projectId: selectedProject.id,
        documentId: reviewDocument.id,
      });
      setReviewDialogOpen(false);
      setReviewDocument(null);
      setReview(null);
      loadDocuments();
    } catch (err) {
      if (tracker.isCurrent('rejectReview', generation)) {
        Logger.error('Error rejecting document review', err);
        setReviewError(extractErrorMessage(err, 'Failed to reject review.'));
      }
    } finally {
      if (tracker.isCurrent('rejectReview', generation))
        setReviewRejecting(false);
    }
  };

  // Retry Processing: a direct, single-click user action for a document
  // whose current status is "error". disciplineId comes from the document's
  // own row, same as handleReviewClick/handleApproveReview/
  // handleRejectReview above.
  const handleRetryProcessing = async (document) => {
    if (retryingDocumentId) return;
    const tracker = requestTrackerRef.current;
    const generation = tracker.begin('retryDocument');
    setRetryingDocumentId(document.id);
    setError('');
    try {
      await DocumentsAPI.retryDocumentProcessing(
        selectedProject.id,
        document.id,
        document.current_version_id,
        document.discipline_id,
        auth.token
      );
      if (!tracker.isCurrent('retryDocument', generation)) return;
      Logger.userAction('Retried Project Documents processing', {
        projectId: selectedProject.id,
        documentId: document.id,
      });
      loadDocuments();
    } catch (err) {
      if (tracker.isCurrent('retryDocument', generation)) {
        Logger.error('Error retrying project document processing', err);
        setError(
          extractErrorMessage(err, 'Failed to retry document processing.')
        );
      }
    } finally {
      if (tracker.isCurrent('retryDocument', generation))
        setRetryingDocumentId(null);
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        data-testid="assistant-three-pane-row"
        sx={{
          flex: 1,
          minHeight: 0,
          // A flex item's default min-width is 'auto' (as wide as its
          // content requires at minimum), which -- unlike minHeight above --
          // has no bearing on the vertical axis but matters here because
          // this row is itself a flex ITEM of the page root's column flex
          // container: without this, the row could refuse to stay within
          // the width the root allots it. Mirrors the same minHeight: 0
          // pattern this codebase already relies on throughout this exact
          // flex chain (see ChatPanel.js's own comment on the same issue).
          minWidth: 0,
          display: 'flex',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <ConversationSidebar
          conversations={conversations}
          conversationsLoading={conversationsLoading}
          conversationId={conversationId}
          disciplines={disciplines}
          renamingConversationId={renamingConversationId}
          renameError={renameError}
          onNewConversation={handleNewConversation}
          onSelectConversation={handleSelectConversation}
          onRenameConversation={handleRenameConversation}
        />

        <ChatPanel
          auth={auth}
          conversationId={conversationId}
          messages={messages}
          messagesLoading={messagesLoading}
          pendingTurn={pendingTurn}
          question={question}
          submitting={
            Boolean(pendingTurn) ||
            (Boolean(conversationId) &&
              updatingScopeConversationId === conversationId)
          }
          onQuestionChange={setQuestion}
          onAskQuestion={handleAskQuestion}
          onRetryQuestion={handleRetryQuestion}
          scopeValue={currentComposerScopeValue}
          disciplines={composerDisciplineOptions}
          disciplinesLoading={disciplinesLoading}
          scopeDisabled={
            conversationId
              ? Boolean(pendingTurn) || Boolean(updatingScopeConversationId)
              : Boolean(pendingTurn)
          }
          onScopeChange={
            conversationId ? handleScopeChange : setDraftDisciplineId
          }
          scopeUpdateError={conversationId ? scopeUpdateError : ''}
          onDownloadToolResult={handleDownloadToolResult}
          onOpenCitation={handleOpenCitation}
          sourceOpenError={sourceOpenError}
          scheduleDownloadError={scheduleDownloadError}
          selectedTool={selectedTool}
          onSelectTool={setSelectedTool}
          onClearTool={() => setSelectedTool(null)}
        />

        {/* Resizes the chat column against the documents pane; neither can
            be dragged below a usable width. */}
        <ColumnResizeHandle
          label="Resize chat"
          minBefore={MIN_CHAT_COLUMN_WIDTH}
          minAfter={MIN_DOCUMENTS_PANE_WIDTH}
          onAfterWidthChange={setDocumentsPaneWidth}
        />

        <DocumentsPane
          width={documentsPaneWidth}
          documents={documents}
          loading={documentsLoading}
          error={documentsError}
          canUpload
          onUploadClick={() => setUploadDialogOpen(true)}
          onFilesDropped={handleFilesDropped}
          onReviewClick={handleReviewClick}
          onRetryClick={handleRetryProcessing}
          onVersionsClick={setVersionsDocument}
          retryingDocumentId={retryingDocumentId}
        />
      </Box>

      <BatchUploadDocumentDialog
        key={uploadDialogKey}
        open={uploadDialogOpen}
        disciplines={disciplines}
        disciplinesLoading={disciplinesLoading}
        onClose={() => {
          setUploadDialogOpen(false);
          setDroppedUpload(null);
        }}
        onUploadAll={handleUploadDocumentBatch}
        initialFiles={droppedUpload?.files}
        initialDocumentTypeId={droppedUpload?.documentTypeId}
      />

      <DocumentReviewDialog
        open={reviewDialogOpen}
        documentTitle={reviewDocument?.title || reviewDocument?.file_name}
        loading={reviewLoading}
        error={reviewError}
        review={review}
        approving={reviewApproving}
        rejecting={reviewRejecting}
        onApprove={handleApproveReview}
        onReject={handleRejectReview}
        onClose={handleCloseReviewDialog}
      />

      {versionsDocument && (
        <DocumentVersionHistoryDialog
          key={versionsDocument.id}
          projectId={selectedProject.id}
          document={versionsDocument}
          token={auth.token}
          onOpenVersion={(documentId, documentVersionId) =>
            fetchAndOpenDocumentVersion(documentId, documentVersionId, null)
          }
          onVersionUploaded={() => loadDocuments()}
          onClose={() => setVersionsDocument(null)}
        />
      )}
    </>
  );
};

/**
 * Project Documents Assistant -- beta, super-admin-only. Scoped to exactly
 * one project (the globally selected project) at a time (see
 * ProjectScopedAssistant above). Discipline is no longer a page-level scope
 * -- see that component's own module docstring.
 *
 * Changing the active project is handled by the existing global project
 * shell (SideNav's project chip -> ProjectSelectionDrawer, which only ever
 * lists projects the caller is already authorized for), not by anything on
 * this page. Disciplines are a global taxonomy (not project-scoped), so
 * they are loaded once, here, independent of which project is selected, and
 * passed down for the composer's scope selector, the sidebar's per-
 * conversation badge, and the upload dialogs' own discipline picker.
 */
const ProjectDocumentsAssistantPage = () => {
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const selectedProject = useSelector((state) => state.project.selectedProject);

  const [disciplines, setDisciplines] = useState([]);
  const [disciplinesLoading, setDisciplinesLoading] = useState(true);
  const [disciplinesError, setDisciplinesError] = useState('');

  // Not scope-sensitive the way conversations/messages/answers are (there
  // is only ever one in-flight load, on mount), so it does not need a
  // tracked channel -- a simple mount-scoped cancellation flag is enough.
  useEffect(() => {
    if (!auth.token) return;
    let cancelled = false;
    setDisciplinesLoading(true);
    DisciplinesAPI.getAll(auth.token)
      .then((data) => {
        if (!cancelled) setDisciplines(data);
      })
      .catch((err) => {
        if (!cancelled) {
          Logger.error('Error loading disciplines', err);
          setDisciplinesError(
            extractErrorMessage(err, 'Failed to load disciplines.')
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDisciplinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.token]);

  return (
    // A plain full-width/full-height Box, not a maxWidth Container -- this
    // page is a full-page working surface (three panes filling the
    // available viewport region), not a centered, list-style admin page
    // with a fixed content width and generous vertical margins. A
    // Container's own maxWidth + py gutters would reserve dead space on
    // wide screens and above/below the three-pane row instead of letting it
    // fill the region the shell (Nav.js) actually allotted this page.
    //
    // boxSizing: 'border-box' is required alongside width: '100%' + padding
    // -- this app does not run MUI's CssBaseline (no global border-box
    // reset, see Login.js's own isolated use of it), so a plain Box
    // defaults to the browser's content-box model, under which `p: 2`
    // padding is added ON TOP OF the 100%-of-parent content width, not
    // absorbed within it -- rendering this page a few pixels wider than
    // the shell actually allotted it and forcing a horizontal scrollbar.
    // maxWidth: '100%' is a defensive backstop against the same class of
    // overflow from any other ancestor/child sizing quirk.
    //
    // SCROLLING OWNERSHIP (three-pane row below): the growing message
    // list owns ordinary chat scrolling (ChatPanel.js's own
    // "chat-messages-scroll-region", fenced by minHeight: 0 at every level
    // of that one nested chain, all the way up through Nav.js's own shell
    // -- see Nav.js's own docstring for why the shell needs a genuinely
    // DEFINITE height, not just a minHeight floor, for any of this to
    // actually cap anything). A long CHAT conversation never grows this
    // page, by design, no matter how long it gets.
    //
    // ConversationSidebar/DocumentsPane each own their OWN internal
    // scrollbar (overflowY: 'auto' on their own root Box) -- the simplest
    // correct behavior for the common case, and for genuinely oversized
    // side-pane content this pane's own scrollbar remains the reachable
    // way to see the rest of it, rather than growing this page. This Box
    // deliberately does NOT set its own overflowY -- a second, redundant
    // scroll container here has no work left to do once the shell above
    // actually caps available height, and risks its own sub-pixel-rounding
    // scrollbar for no benefit.
    <Box
      data-testid="assistant-page-root"
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        maxWidth: '100%',
        p: 2,
      }}
    >
      {disciplinesError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setDisciplinesError('')}
        >
          {disciplinesError}
        </Alert>
      )}

      {!selectedProject ? (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => dispatch(projectActions.openProjectDrawer())}
            >
              Select Project
            </Button>
          }
        >
          Select a project to use the Project Documents Assistant.
        </Alert>
      ) : (
        <ProjectScopedAssistant
          key={selectedProject.id}
          auth={auth}
          selectedProject={selectedProject}
          disciplines={disciplines}
          disciplinesLoading={disciplinesLoading}
        />
      )}
    </Box>
  );
};

export default ProjectDocumentsAssistantPage;
